/**
 * Паспорт договора: номер и наличие фото видны всем вошедшим; сами фото — по праву `passport_view`
 * (каждый просмотр ручка пишет в журнал). Берём последний документ каждого типа: 1 — «1 стр», 2 — «Прописка».
 * Отдельных кнопок нет: при праве и фото кликается сама отметка «1 стр ✓» / «прописка ✓» (владелец 23.10.26).
 */
import { Download } from "lucide-react";
import { useEffect, useState } from "react";

import { MediaViewer } from "@/components/MediaViewer";
import { PASSPORT_VIEW, useCan } from "@/lib/access";
import { type RawContract, passportPhotoUrl } from "@/lib/contract";

const TYPES = [
  { type: 1, label: "1 стр", title: "1 стр", ok: "page1" },
  { type: 2, label: "прописка", title: "Прописка", ok: "page2" },
] as const;

export function PassportBlock({ dogovorId, passport }: { dogovorId: number; passport: RawContract["passport"] }) {
  const can = useCan(PASSPORT_VIEW);
  const [url, setUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Не оставляем object URL висеть при уходе со вкладки.
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const latest = (type: number) => passport.docs.filter((d) => d.type === type).sort((a, b) => b.id - a.id)[0];
  const open = async (type: number, label: string) => {
    const doc = latest(type);
    if (!doc) return;
    setBusy(type);
    setErr(null);
    try {
      setCaption(`Паспорт · ${label}`);
      setUrl(await passportPhotoUrl(dogovorId, doc.id));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не удалось открыть фото");
    } finally {
      setBusy(null);
    }
  };
  const mark = (ok: boolean) => <span className={ok ? "text-ok" : "text-err"}>{ok ? "✓" : "✗"}</span>;
  return (
    <div className="flex flex-col gap-1" data-annot={`паспорт договора ${dogovorId}`}>
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-fg-muted">Паспорт:</span>
        {passport.number ? <span className="font-mono">{passport.number}</span> : <span className="text-err">не указан</span>}
        <span className="text-fg-muted">
          {TYPES.map((t, i) => {
            const ok = passport[t.ok];
            const label = (
              <>
                {t.label} {mark(ok)}
              </>
            );
            return (
              <span key={t.type}>
                {i > 0 && " · "}
                {can && latest(t.type) ? (
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => void open(t.type, t.title)}
                    className="text-accent-text underline decoration-dotted underline-offset-2 hover:decoration-solid disabled:opacity-50"
                    title="Открыть фото — смотреть и скачать"
                  >
                    {busy === t.type ? "открываю…" : label}
                  </button>
                ) : (
                  label
                )}
              </span>
            );
          })}
        </span>
      </div>
      {err && <div className="text-err">{err}</div>}
      {url && (
        <MediaViewer
          items={[{ kind: "photo", url, caption }]}
          start={0}
          onClose={() => setUrl(null)}
          actions={() => (
            <a
              href={url}
              download={`pasport-${dogovorId}-${caption.endsWith("1 стр") ? "1str" : "propiska"}.jpg`}
              title="Скачать фото"
              className="grid size-10 place-items-center rounded-full bg-white/10 hover:bg-white/20"
            >
              <Download size={18} aria-hidden />
            </a>
          )}
        />
      )}
    </div>
  );
}
